import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import { useTheme } from '../context/ThemeContext';
import { useTranslation } from '../context/LanguageContext';
import { Home, MessageCircle, Trophy, User } from 'lucide-react';
import { hapticLight } from '../lib/haptics';

export function BottomNav() {
  const location = useLocation();
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const { t } = useTranslation();
  
  // Track which button was clicked for instant visual feedback
  const [clickedPath, setClickedPath] = useState(null);
  const clickTimeoutRef = useRef(null);
  
  // Nav items with translation keys - using /app prefix
  const NAV_ITEMS = [
    { path: '/app/dashboard', icon: Home, labelKey: 'nav.home' },
    { path: '/app/feed', icon: MessageCircle, labelKey: 'nav.feed' },
    { path: 'fab', isCenterFab: true },
    { path: '/app/challenges', icon: Trophy, labelKey: 'nav.challenges' },
    { path: '/app/profilo', icon: User, labelKey: 'nav.profile' },
  ];
  
  // Reset clicked state when location changes
  useEffect(() => {
    setClickedPath(null);
  }, [location.pathname]);
  
  const handleNavClick = (e, path) => {
    e.preventDefault();
    
    // Instant visual feedback
    setClickedPath(path);
    
    // Clear any pending timeout
    if (clickTimeoutRef.current) {
      clearTimeout(clickTimeoutRef.current);
    }
    
    // Navigate immediately
    hapticLight();
    navigate(path);
    
    // Keep the visual state for at least 300ms to ensure smooth transition
    clickTimeoutRef.current = setTimeout(() => {
      setClickedPath(null);
    }, 300);
  };

  return (
    <nav className={`bottom-nav ${isDark ? 'dark' : ''}`} data-testid="bottom-nav">
      <div className="grid grid-cols-5 items-center px-2">
        {NAV_ITEMS.map((item) => {
          // Center FAB button for Add Post - Speech bubble with + inside
          if (item.isCenterFab) {
            return (
              <div
                key="fab"
                className="flex flex-col items-center justify-center"
                style={{ zIndex: 150 }}
              >
                <button
                  onClick={() => { hapticLight(); navigate('/app/nuovo-post'); }}
                  className="w-14 h-14 rounded-full flex items-center justify-center shadow-lg bg-[#E8334A] relative active:scale-95 transition-transform"
                  style={{ zIndex: 150, marginTop: '-6px' }}
                  data-testid="fab-button"
                >
                  {/* Custom speech bubble with + icon */}
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                    <line x1="12" y1="7" x2="12" y2="13" />
                    <line x1="9" y1="10" x2="15" y2="10" />
                  </svg>
                </button>
              </div>
            );
          }

          const Icon = item.icon;
          const isCurrentPage = location.pathname === item.path || (item.path === '/app/dashboard' && location.pathname === '/app');
          const isActive = clickedPath === item.path || (clickedPath === null && isCurrentPage);
          const label = t(item.labelKey).toUpperCase();
          
          return (
            <a
              key={item.path}
              href={item.path}
              onClick={(e) => handleNavClick(e, item.path)}
              className={`nav-item flex flex-col items-center justify-center py-2 ${isActive ? 'nav-active' : ''}`}
              data-testid={`nav-${item.labelKey.split('.')[1]}`}
              data-path={item.path}
            >
              <Icon className={`w-6 h-6 nav-icon ${
                isActive 
                  ? 'text-[#1d3557]' 
                  : isDark ? 'text-white/50' : 'text-gray-400'
              }`} />
              <span className={`text-[10px] font-medium mt-1 nav-label text-center ${
                isActive 
                  ? 'text-[#1d3557]' 
                  : isDark ? 'text-white/50' : 'text-gray-400'
              }`}>
                {label}
              </span>
            </a>
          );
        })}
      </div>
    </nav>
  );
}
